import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Panel,
  PanelType,
  Dialog,
  DialogType,
  DialogFooter,
  MessageBar,
  MessageBarType,
  Separator,
  Spinner,
  ActionButton,
} from "@fluentui/react";
import { Link } from 'react-router-dom';
import { styles } from "./ProposalDashboard";

/**
 * ClientsManager (Fluent UI v8, React 17)
 * - Lists clients with search/sort/pagination
 * - Create / Update via right-side Panel
 * - Delete with confirmation dialog
 * - Expects /api/clients endpoints (same JSON shape as earlier routers)
 */
export default function ClientsManager() {
  // table state
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1, sort: "name", order: "asc", search: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);

  // panel/form state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("create"); // 'create' | 'edit'
  const [form, setForm] = useState(emptyClient());

  // delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}

  // toast
  const [toast, setToast] = useState(null); // {type, text}

  // columns
  const columns = useMemo(() => {
    const onColumnClick = (ev, col) => {
      if (!col) return;
      const isSame = meta.sort === col.fieldName;
      const nextOrder = isSame && meta.order === "asc" ? "desc" : "asc";
      setMeta((m) => ({ ...m, sort: col.fieldName, order: nextOrder }));
    };
    return [
      { 
        key: "name", 
        name: "Name", 
        fieldName: "name", 
        minWidth: 180, 
        isResizable: true, 
        onColumnClick,
        onRender: (item) => {
          const text = item.name || `Client: ${item.id}`;
          return (
            <Link to={`/clients/${item.id}`} styles={{ root: { overflowWrap: 'anywhere' } }}>
              {text}
            </Link>
          );
        },
      },
      { key: "sector", name: "Sector", fieldName: "sector", minWidth: 120, isResizable: true, onColumnClick },
      {
        key: "website_url",
        name: "Website",
        fieldName: "website_url",
        minWidth: 180,
        isResizable: true,
        onRender: (i) => (i.website_url ? (
          <a href={i.website_url} target="_blank" rel="noopener noreferrer">{i.website_url}</a>
        ) : "—"),
        onColumnClick,
      },
      { key: "industry_category", name: "Industry", fieldName: "industry_category", minWidth: 140, isResizable: true, onColumnClick },
    ];
  }, [meta.sort, meta.order]);

  // data fetch
  const fetchClients = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: overrides.page ?? meta.page,
        limit: overrides.limit ?? meta.limit,
        sort: overrides.sort ?? meta.sort,
        order: overrides.order ?? meta.order,
        search: overrides.search ?? debouncedSearch,
      };
      const res = await axios.get("/api/clients", { params });
      setItems(res.data?.data || []);
      setMeta(res.data?.meta || { page: 1, limit: 10, total: 0, totalPages: 1, sort: "name", order: "asc", search: "" });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [meta.page, meta.limit, meta.sort, meta.order, debouncedSearch]);

  useEffect(() => { fetchClients({ page: 1, search: debouncedSearch }); }, [debouncedSearch, fetchClients]);
  useEffect(() => { fetchClients(); }, [meta.page, meta.limit, meta.sort, meta.order, fetchClients]);

  // CRUD helpers
  function openCreate() {
    setPanelMode("create");
    setForm(emptyClient());
    setPanelOpen(true);
  }

  function openEdit(row) {
    setPanelMode("edit");
    setForm({ ...emptyClient(), ...row });
    setPanelOpen(true);
  }

  async function saveForm() {
    try {
      if (!form.name || !String(form.name).trim()) {
        setToast({ type: MessageBarType.error, text: "Name is required" });
        return;
      }
      const payload = normalizePayload(form);
      if (panelMode === "create") {
        const res = await axios.post("/api/clients", payload);
        setToast({ type: MessageBarType.success, text: `Created ${res.data?.name || "client"}` });
      } else {
        const res = await axios.patch(`/api/clients/${form.id}`, payload);
        setToast({ type: MessageBarType.success, text: `Updated ${res.data?.name || "client"}` });
      }
      setPanelOpen(false);
      await fetchClients();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Save failed" });
    }
  }

  async function confirmDelete() {
    try {
      if (!deleteTarget) return;
      await axios.delete(`/api/clients/${deleteTarget.id}`);
      setToast({ type: MessageBarType.success, text: `Deleted ${deleteTarget.name}` });
      setDeleteTarget(null);
      await fetchClients();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Delete failed" });
    }
  }

  return (
    <Stack tokens={{ childrenGap: 16 }} className={styles.root}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="end" tokens={{ childrenGap: 12 }}>
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="xxLarge">Clients</Text>
          <Text variant="small">Manage organizations used across projects and proposals.</Text>
        </Stack>
        <ActionButton iconProps={{ iconName: "Add" }} text="New client" onClick={openCreate} />
      </Stack>

      <Stack className={styles.sectionCard} horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
        <TextField
          label="Search"
          value={search}
          onChange={(_, v) => setSearch(v || "")}
          placeholder="Search name, sector, region"
          styles={{ root: { width: 320 } }}
        />
        <TextField
          label="Page size"
          value={String(meta.limit)}
          onChange={(_, v) => setMeta((m) => ({ ...m, limit: clampInt(v, 1, 200) }))}
          styles={{ root: { width: 120 } }}
        />
        <DefaultButton onClick={() => fetchClients({ page: 1, search })} text="Apply" />
      </Stack>

      {toast && (
        <MessageBar messageBarType={toast.type} isMultiline={false} onDismiss={() => setToast(null)}>
          {toast.text}
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError("")}>{error}</MessageBar>
      )}

      <Stack className={styles.sectionCard}>
        {loading ? (
          <Stack horizontal horizontalAlign="center" styles={{ root: { padding: 40 } }}>
            <Spinner label="Loading clients…" />
          </Stack>
        ) : (
          <DetailsList
            items={items}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />
        )}
      </Stack>

      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="small">Page {meta.page} of {meta.totalPages} · {meta.total} total</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <ActionButton text="Prev" disabled={meta.page <= 1} onClick={() => setMeta((m) => ({ ...m, page: Math.max(1, m.page - 1) }))} />
          <ActionButton text="Next" disabled={meta.page >= meta.totalPages} onClick={() => setMeta((m) => ({ ...m, page: Math.min(m.totalPages, m.page + 1) }))} />
        </Stack>
      </Stack>

      {/* Create / Edit Panel */}
      <Panel
        isOpen={panelOpen}
        onDismiss={() => setPanelOpen(false)}
        type={PanelType.medium}
        headerText={panelMode === "create" ? "New client" : `Edit: ${form.name || "(no name)"}`}
        closeButtonAriaLabel="Close"
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <RequiredTextField label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
          <TextField label="Parent organization" value={form.parent_organization || ""} onChange={(_, v) => setForm((f) => ({ ...f, parent_organization: v || null }))} />
          <TextField label="Sector" value={form.sector || ""} onChange={(_, v) => setForm((f) => ({ ...f, sector: v || null }))} />
          <TextField label="Region" value={form.region || ""} onChange={(_, v) => setForm((f) => ({ ...f, region: v || null }))} />
          <TextField label="Website URL" value={form.website_url || ""} onChange={(_, v) => setForm((f) => ({ ...f, website_url: v || null }))} />
          <TextField label="Industry category" value={form.industry_category || ""} onChange={(_, v) => setForm((f) => ({ ...f, industry_category: v || null }))} />
          <TextField label="Description" multiline autoAdjustHeight value={form.description || ""} onChange={(_, v) => setForm((f) => ({ ...f, description: v || null }))} />

          <Separator />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton text={panelMode === "create" ? "Create" : "Save changes"} onClick={saveForm} />
            <DefaultButton text="Cancel" onClick={() => setPanelOpen(false)} />
          </Stack>
        </Stack>
      </Panel>

      {/* Delete confirm */}
      <Dialog
        hidden={!deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
        dialogContentProps={{ type: DialogType.largeHeader, title: "Delete client", subText: deleteTarget ? `Delete ${deleteTarget.name}? This cannot be undone.` : "" }}
      >
        <DialogFooter>
          <PrimaryButton onClick={confirmDelete} text="Delete" />
          <DefaultButton onClick={() => setDeleteTarget(null)} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
}

// ===== helpers =====
function emptyClient() {
  return {
    id: undefined,
    name: "",
    parent_organization: null,
    sector: null,
    region: null,
    website_url: null,
    description: null,
    industry_category: null,
  };
}

function clampInt(v, min, max) {
  const n = parseInt(String(v || "").replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function useDebouncedValue(value, delayMs) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function RequiredTextField({ label, value, onChange }) {
  const [touched, setTouched] = useState(false);
  const errorMessage = touched && !String(value || "").trim() ? `${label} is required` : undefined;
  return (
    <TextField
      required
      label={label}
      value={value}
      onChange={(_, v) => onChange(v || "")}
      onBlur={() => setTouched(true)}
      errorMessage={errorMessage}
    />
  );
}

function previewText(text, max) {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max) + "…";
}

function normalizePayload(f) {
  const p = { ...f };
  Object.keys(p).forEach((k) => {
    if (p[k] === "") p[k] = null;
  });
  return p;
}
